import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Pagination,
  Popover,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { RefreshData } from "components/helper";
import DeleteModal from "components/Modals/DeleteModal";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { useEffect, useMemo, useState } from "react";
import { FaFilter, FaPlusCircle, FaTable, FaTh } from "react-icons/fa";
import { Link, useSearchParams } from "react-router-dom";
import { getThemedTabsSx } from "theme/tabsTheme";
import { getPaginatedClientData } from "utils/clientService";
import { isOrgSuperAdmin } from "utils/permissionUtils";
import {
  ACTIVE_TASK_STATUS_FILTER,
  serializeStatusPages,
  TASK_STATUS_KEYS,
  useDeleteTask,
  useGroupedTasksPage,
} from "utils/taskService";
import KanbanView from "./KanbanView";
import TableView from "./TableView";

const ACTIVE_STATUSES = ACTIVE_TASK_STATUS_FILTER;

const STATUS_OPTIONS = [
  { label: "All statuses", value: "" },
  { label: "All open (Pending, In Progress, On Hold)", value: ACTIVE_STATUSES },
  { label: "Pending", value: "PENDING" },
  { label: "In Progress", value: "IN_PROGRESS" },
  { label: "On Hold", value: "HOLD" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Cancelled", value: "CANCELLED" },
];

const SORT_OPTIONS = [
  { label: "Due date & priority", sortBy: "deadlinePriority", sortDirection: "ASC" },
  { label: "Updated (newest)", sortBy: "updatedDate", sortDirection: "DESC" },
  { label: "Updated (oldest)", sortBy: "updatedDate", sortDirection: "ASC" },
  { label: "Deadline (soonest)", sortBy: "deadline", sortDirection: "ASC" },
  { label: "Deadline (latest)", sortBy: "deadline", sortDirection: "DESC" },
];

const PAGE_SIZE_OPTIONS = [5, 10, 20, 40, 50, 100];

// Kanban columns need more cards to be useful than a table page does.
const DEFAULT_PAGE_SIZE_BY_VIEW = { kanban: 20, table: 20 };

// Page size is applied per status by the backend, so this is 0 for every status rather than a
// single shared page index.
const EMPTY_STATUS_PAGES = TASK_STATUS_KEYS.reduce((acc, status) => ({ ...acc, [status]: 0 }), {});

const EMPTY_FILTERS = {
  orgWide: false,
  search: "",
  assignedTo: null,
  assignedBy: null,
  client: null,
  status: "",
  sortBy: "deadlinePriority",
  sortDirection: "ASC",
  page: 0,
  statusPages: EMPTY_STATUS_PAGES,
  size: 20,
};

// The board opens on open work only (Pending, In Progress, On Hold); "All statuses" is one pick
// away in the filters. The table has its own Open / Closed tabs, so it starts unfiltered.
const defaultStatusFor = (viewMode) => (viewMode === "kanban" ? ACTIVE_STATUSES : "");

const Tasks = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [openConfirmationModal, setOpenConfirmationModal] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [{ token, usersList }, { refetchUsers }] = useKothar();
  const [tab, setTab] = useState(0);
  const initialViewMode = searchParams.get("view") === "table" ? "table" : "kanban";
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [hasCustomPageSize, setHasCustomPageSize] = useState(false);
  const [filters, setFilters] = useState(() => ({
    ...EMPTY_FILTERS,
    status: defaultStatusFor(initialViewMode),
    size: DEFAULT_PAGE_SIZE_BY_VIEW[initialViewMode],
  }));
  const canOverseeOrgTasks = useMemo(() => isOrgSuperAdmin(), []);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [clientOptions, setClientOptions] = useState([]);
  const [clientSearch, setClientSearch] = useState(null);
  const [loadingClients, setLoadingClients] = useState(false);
  const [filtersAnchorEl, setFiltersAnchorEl] = useState(null);

  useEffect(() => {
    if (!usersList?.length) refetchUsers?.();
  }, [usersList?.length, refetchUsers]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      search: debouncedSearch,
      page: 0,
      statusPages: EMPTY_STATUS_PAGES,
    }));
  }, [debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setLoadingClients(true);
      try {
        const page = await getPaginatedClientData({
          pageIndex: 0,
          pageSize: 20,
          searchText: clientSearch.trim() || undefined,
        });
        setClientOptions(Array.isArray(page?.content) ? page.content : []);
      } catch {
        setClientOptions([]);
      } finally {
        setLoadingClients(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearch]);

  useEffect(() => {
    // The table pages all statuses together while the kanban pages each column on its own, so a
    // per-column page carried across the switch would leave the table showing mismatched pages.
    setFilters((prev) => ({ ...prev, page: 0, statusPages: EMPTY_STATUS_PAGES }));
    const currentView = searchParams.get("view");
    if (currentView !== viewMode) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("view", viewMode);
      setSearchParams(newParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // Kanban shows every status column, so it only needs the bigger page size; the table
  // lets its tabs drive status and must not inherit kanban's combined-status filter.
  useEffect(() => {
    setFilters((prev) => {
      const next = { ...prev, page: 0 };
      if (!hasCustomPageSize) next.size = DEFAULT_PAGE_SIZE_BY_VIEW[viewMode];
      if (viewMode === "table" && prev.status === ACTIVE_STATUSES) next.status = "";
      if (viewMode === "kanban" && !prev.status) next.status = ACTIVE_STATUSES;
      return next;
    });
  }, [viewMode, hasCustomPageSize]);

  const userOptions = useMemo(
    () =>
      (usersList ?? []).map((u) => ({
        id: u.id,
        email: u.email,
        label: u.name || u.email || `User #${u.id}`,
      })),
    [usersList]
  );

  const queryParams = useMemo(() => {
    const effectiveStatus =
      filters.status ||
      (viewMode === "table" ? (tab === 1 ? "COMPLETED,CANCELLED" : ACTIVE_STATUSES) : "");
    return {
      page: filters.page,
      statusPages: serializeStatusPages(filters.statusPages) || undefined,
      size: filters.size,
      sortBy: filters.sortBy,
      sortDirection: filters.sortDirection,
      search: filters.search || undefined,
      assignedTo: filters.assignedTo?.email || undefined,
      assignedBy: filters.assignedBy?.email || undefined,
      clientId: filters.client?.id ?? undefined,
      status: effectiveStatus || undefined,
      orgWide: filters.orgWide || undefined,
    };
  }, [filters, tab, viewMode]);

  const {
    data: groupedTasks,
    refetch,
    isFetching,
  } = useGroupedTasksPage(queryParams, {
    enabled: !!token,
  });

  const activeStatuses = useMemo(() => groupedTasks?.statuses ?? TASK_STATUS_KEYS, [groupedTasks]);
  const taskGroups = useMemo(() => groupedTasks?.groups ?? {}, [groupedTasks]);
  // Table view stacks every status group into one list, in board order, so each status still
  // contributes its own page of `size` rows instead of competing for a single shared page.
  const tasksData = useMemo(
    () => activeStatuses.flatMap((status) => taskGroups[status]?.content ?? []),
    [activeStatuses, taskGroups]
  );
  const totalItems = Number(groupedTasks?.totalItems) || 0;
  const totalPages = Math.max(
    1,
    ...activeStatuses.map((status) => Number(taskGroups[status]?.totalPages) || 0)
  );

  const { mutate: deleteTask } = useDeleteTask({
    onSuccess: () => {
      setOpenConfirmationModal({ state: false, id: null });
      refetch();
    },
  });

  const setFilter = (field, value, resetPage = true) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      ...(resetPage ? { page: 0, statusPages: EMPTY_STATUS_PAGES } : {}),
    }));
  };

  // Table pager walks every status group forward together; the kanban pages one column at a time.
  const setSharedPage = (page) =>
    setFilters((prev) => ({ ...prev, page, statusPages: EMPTY_STATUS_PAGES }));

  const setStatusPage = (status, page) =>
    setFilters((prev) => ({
      ...prev,
      statusPages: { ...prev.statusPages, [status]: Math.max(0, page) },
    }));

  const clearFilters = () => {
    setSearchInput("");
    setDebouncedSearch("");
    setClientSearch("");
    setHasCustomPageSize(false);
    setFilters({
      ...EMPTY_FILTERS,
      status: defaultStatusFor(viewMode),
      size: DEFAULT_PAGE_SIZE_BY_VIEW[viewMode],
    });
    setTab(0);
  };

  const activeFilterCount = [
    filters.assignedTo,
    filters.assignedBy,
    filters.client,
    // The view's own default is not a filter the user set.
    filters.status && filters.status !== defaultStatusFor(viewMode),
    filters.sortBy !== EMPTY_FILTERS.sortBy ||
      filters.sortDirection !== EMPTY_FILTERS.sortDirection,
    filters.size !== DEFAULT_PAGE_SIZE_BY_VIEW[viewMode],
  ].filter(Boolean).length;

  const hasActiveFilters = Boolean(filters.search) || activeFilterCount > 0;

  const handleTabChange = (_, newTab) => {
    setTab(newTab);
    setFilter("status", "");
  };

  const deleteData = () => {
    if (!openConfirmationModal?.id) return;
    deleteTask(openConfirmationModal.id);
  };

  const handleClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const activeSortValue =
    SORT_OPTIONS.find(
      (o) => o.sortBy === filters.sortBy && o.sortDirection === filters.sortDirection
    )?.label || SORT_OPTIONS[0].label;

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4 h-full">
        <div className="bg-app-surface rounded-t mb-0 px-4 py-3 border-0">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-lg text-app-text">
                Tasks ({totalItems || tasksData.length})
              </h3>
              <RefreshData refreshData={refetch} />
              {isFetching && !isFetching && <CircularProgress size={16} />}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode !== null) setViewMode(newMode);
                }}
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    borderColor: "var(--app-border)",
                    color: "var(--app-text-secondary)",
                    "&.Mui-selected": {
                      backgroundColor: "var(--app-primary)",
                      color: "white",
                      "&:hover": { backgroundColor: "var(--app-primary)", opacity: 0.9 },
                    },
                  },
                }}
              >
                <ToggleButton value="table" aria-label="table view">
                  <FaTable className="mr-2" />
                  Table
                </ToggleButton>
                <ToggleButton value="kanban" aria-label="kanban view">
                  <FaTh className="mr-2" />
                  Kanban
                </ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="contained"
                startIcon={<FaPlusCircle />}
                component={Link}
                to="/admin/tasks/add"
              >
                Add Tasks
              </Button>
            </div>
          </div>

          <Stack spacing={1.5} className="mt-4">
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <TextField
                size="small"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search title, client, comment…"
                sx={{ minWidth: { xs: "100%", sm: 260 }, flex: { sm: "1 1 260px" }, maxWidth: 420 }}
              />
              {canOverseeOrgTasks && (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={filters.orgWide}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          orgWide: e.target.checked,
                          page: 0,
                          statusPages: EMPTY_STATUS_PAGES,
                        }))
                      }
                    />
                  }
                  label="All organization tasks"
                  slotProps={{ typography: { variant: "body2" } }}
                />
              )}
              <Badge
                color="primary"
                badgeContent={activeFilterCount}
                invisible={!activeFilterCount}
              >
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FaFilter size={12} />}
                  onClick={(e) => setFiltersAnchorEl(e.currentTarget)}
                  sx={{
                    height: 40,
                    borderColor: "var(--app-border)",
                    color: "var(--app-text-secondary)",
                  }}
                >
                  Filters
                </Button>
              </Badge>
              {hasActiveFilters && (
                <Button size="small" variant="text" onClick={clearFilters} sx={{ height: 40 }}>
                  Clear all
                </Button>
              )}
            </Stack>

            <Popover
              open={Boolean(filtersAnchorEl)}
              anchorEl={filtersAnchorEl}
              onClose={() => setFiltersAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              <Stack spacing={2} sx={{ p: 2.5, width: 300 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Filter tasks
                </Typography>
                <Autocomplete
                  size="small"
                  options={userOptions}
                  getOptionLabel={(o) => o.label}
                  value={filters.assignedTo}
                  onChange={(_, val) => setFilter("assignedTo", val)}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={(params) => <TextField {...params} label="Assigned to" />}
                />
                <Autocomplete
                  size="small"
                  options={userOptions}
                  getOptionLabel={(o) => o.label}
                  value={filters.assignedBy}
                  onChange={(_, val) => setFilter("assignedBy", val)}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={(params) => <TextField {...params} label="Assigned by" />}
                />
                <Autocomplete
                  size="small"
                  options={clientOptions}
                  loading={loadingClients}
                  filterOptions={(x) => x}
                  getOptionLabel={(o) =>
                    o?.passportNumber ? `${o.name} (${o.passportNumber})` : o?.name || ""
                  }
                  value={filters.client}
                  onChange={(_, val) => setFilter("client", val)}
                  onInputChange={(_, value, reason) => {
                    if (reason !== "reset") setClientSearch(value);
                  }}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={(params) => <TextField {...params} label="Client" />}
                />
                <FormControl size="small" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={filters.status}
                    onChange={(e) => {
                      const next = e.target.value;
                      setFilter("status", next);
                      if (next === "COMPLETED" || next === "CANCELLED") setTab(1);
                      else if (next) setTab(0);
                    }}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value || "all"} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Divider />
                <FormControl size="small" fullWidth>
                  <InputLabel>Sort</InputLabel>
                  <Select
                    label="Sort"
                    value={activeSortValue}
                    onChange={(e) => {
                      const opt = SORT_OPTIONS.find((o) => o.label === e.target.value);
                      if (!opt) return;
                      setFilters((prev) => ({
                        ...prev,
                        sortBy: opt.sortBy,
                        sortDirection: opt.sortDirection,
                        page: 0,
                        statusPages: EMPTY_STATUS_PAGES,
                      }));
                    }}
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <MenuItem key={opt.label} value={opt.label}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Page size</InputLabel>
                  <Select
                    label="Page size"
                    value={filters.size}
                    onChange={(e) => {
                      setHasCustomPageSize(true);
                      setFilter("size", Number(e.target.value));
                    }}
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Popover>

            {hasActiveFilters && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {filters.search && (
                  <Chip
                    size="small"
                    label={`Search: ${filters.search}`}
                    onDelete={() => {
                      setSearchInput("");
                      setDebouncedSearch("");
                      setFilter("search", "");
                    }}
                  />
                )}
                {filters.assignedTo && (
                  <Chip
                    size="small"
                    label={`To: ${filters.assignedTo.label}`}
                    onDelete={() => setFilter("assignedTo", null)}
                  />
                )}
                {filters.assignedBy && (
                  <Chip
                    size="small"
                    label={`By: ${filters.assignedBy.label}`}
                    onDelete={() => setFilter("assignedBy", null)}
                  />
                )}
                {filters.client && (
                  <Chip
                    size="small"
                    label={`Client: ${filters.client.name}`}
                    onDelete={() => setFilter("client", null)}
                  />
                )}
                {filters.status && (
                  <Chip
                    size="small"
                    label={`Status: ${
                      STATUS_OPTIONS.find((s) => s.value === filters.status)?.label ||
                      filters.status
                    }`}
                    onDelete={() => {
                      setFilter("status", "");
                      setTab(0);
                    }}
                  />
                )}
              </Stack>
            )}
          </Stack>
        </div>

        {isFetching && !tasksData.length ? (
          <div className="flex justify-center items-center py-24">
            <CircularProgress />
          </div>
        ) : (
          <>
            {viewMode === "table" && (
              <>
                <Tabs
                  value={tab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons
                  allowScrollButtonsMobile
                  role="navigation"
                  sx={getThemedTabsSx()}
                >
                  <Tab label="Active Tasks" sx={{ fontWeight: "bold", fontSize: 16 }} />
                  <Tab label="Completed / Cancelled" sx={{ fontWeight: "bold", fontSize: 16 }} />
                </Tabs>
                <TabPanel value={tab} index={tab}>
                  <TableView
                    tasksData={tasksData}
                    isFetching={isFetching}
                    anchorEl={anchorEl}
                    selectedRow={selectedRow}
                    open={open}
                    id={id}
                    handleClick={handleClick}
                    handleClose={handleClose}
                    setOpenConfirmationModal={setOpenConfirmationModal}
                  />
                </TabPanel>
              </>
            )}
            {viewMode === "kanban" && (
              <KanbanView
                taskGroups={taskGroups}
                statuses={activeStatuses}
                onStatusPageChange={setStatusPage}
                token={token}
                refetch={refetch}
                handleClick={handleClick}
                isFetching={isFetching}
                API_URL={API_URL}
                onShowClosedTasks={() => setFilter("status", "")}
                statusFilter={filters.status}
              />
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 px-2 py-4 bg-app-surface border-t border-app-border">
              <Typography variant="body2" color="text.secondary">
                Showing {tasksData.length} of {totalItems} task{totalItems === 1 ? "" : "s"}
                {` · up to ${filters.size} per status`}
              </Typography>
              {viewMode === "table" && (
                <Pagination
                  color="primary"
                  page={filters.page + 1}
                  count={totalPages}
                  onChange={(_, page) => setSharedPage(page - 1)}
                  disabled={isFetching}
                />
              )}
            </div>
          </>
        )}
      </div>

      {openConfirmationModal.state && (
        <DeleteModal
          open={openConfirmationModal.state}
          item="Task"
          handleCancel={() => setOpenConfirmationModal({ state: false, id: null })}
          handleDelete={() => deleteData()}
        />
      )}
    </div>
  );
};

export default Tasks;

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 0 }}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}
